import { EU_UMBRELLA_ORIGIN_SRC } from './constants/DOMAINS'
import { Country } from './types'

// Canonical EU/EEA membership: the 27 EU member states + 3 additional EEA
// countries (Iceland, Liechtenstein, Norway). Stored as [alpha-2, alpha-3]
// tuples so the country-list filter (alpha-3) and the origin-path detection
// (alpha-2) derive from one source and never drift. Hardcoded rather than
// derived from the BE `european` flag — that flag is unreliable per project
// history.
const EU_EEA_COUNTRIES: ReadonlyArray<readonly [string, string]> = [
    // EU-27
    ['AT', 'AUT'], ['BE', 'BEL'], ['BG', 'BGR'], ['HR', 'HRV'], ['CY', 'CYP'],
    ['CZ', 'CZE'], ['DK', 'DNK'], ['EE', 'EST'], ['FI', 'FIN'], ['FR', 'FRA'],
    ['DE', 'DEU'], ['GR', 'GRC'], ['HU', 'HUN'], ['IE', 'IRL'], ['IT', 'ITA'],
    ['LV', 'LVA'], ['LT', 'LTU'], ['LU', 'LUX'], ['MT', 'MLT'], ['NL', 'NLD'],
    ['PL', 'POL'], ['PT', 'PRT'], ['RO', 'ROU'], ['SK', 'SVK'], ['SI', 'SVN'],
    ['ES', 'ESP'], ['SE', 'SWE'],
    // EEA non-EU
    ['IS', 'ISL'], ['LI', 'LIE'], ['NO', 'NOR'],
]

// ISO 3166-1 alpha-3 codes — consumed by the sign-up country-list filter.
export const EU_EEA_COUNTRY_CODES: ReadonlySet<string> = new Set(EU_EEA_COUNTRIES.map(([, code3]) => code3))

// Lower-cased ISO 3166-1 alpha-2 codes — used to test an IP-geolocated
// country_code (the BE returns it lower-cased, e.g. "de", "rs").
const EU_EEA_COUNTRY_CODES_ALPHA2: ReadonlySet<string> = new Set(EU_EEA_COUNTRIES.map(([code2]) => code2.toLowerCase()))

// True when an IP-geolocated country denotes the EU/EEA. Private helper behind
// isEuRestricted (the IP half of the onboarding EU restriction).
const isEuEeaCountryCode = (countryCode?: string | null): boolean =>
    countryCode != null && EU_EEA_COUNTRY_CODES_ALPHA2.has(countryCode.toLowerCase())

// Lower-cased origin-path tokens that denote the EU: the /eu umbrella plus
// every EU/EEA country's alpha-2 code. originSrc is always stored lower-cased,
// so this set is too and membership is a direct lookup with no re-casing.
const EU_ORIGIN_SRC_TOKENS: ReadonlySet<string> = new Set([
    EU_UMBRELLA_ORIGIN_SRC,
    ...EU_EEA_COUNTRY_CODES_ALPHA2,
])

// True when an origin-path segment denotes the EU. Single predicate shared by
// detection time (saga) and read time (useIsEuRestricted) so the rule lives once.
export const isEuOriginSrc = (originSrc: string | null): boolean =>
    originSrc !== null && EU_ORIGIN_SRC_TOKENS.has(originSrc)

// Origin-or-IP EU rule — the fallback half of useIsEuRestricted (which first
// defers to a logged-in profile country). An explicit marketing origin (?src and
// /referrer, already collapsed into originSrc at detection) decides — EU origin →
// EU, non-EU origin → not EU, regardless of IP — and only with no origin does the
// IP geolocation decide.
export const isEuRestricted = (originSrc: string | null, ipCountryCode?: string | null): boolean =>
    originSrc !== null ? isEuOriginSrc(originSrc) : isEuEeaCountryCode(ipCountryCode)

export const filterToEuEea = <T extends Pick<Country, 'code3'>>(countries: T[]): T[] =>
    countries.filter(c => EU_EEA_COUNTRY_CODES.has(c.code3))
